/**
 * ClassInsights — the "Teach Next" lesson-planning view.
 * Aggregates every student's weak points into a ranked list of grammar topics
 * the whole class struggles with, so the teacher knows what to teach next.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle, FiChevronDown, FiChevronRight, FiTarget, FiUsers,
} from 'react-icons/fi';
import teacherAPI from '../services/api';

const SEV_COLOR = {
  high: '#ff6b6b',
  medium: '#ffb340',
  low: '#2997ff',
};

function ClassInsights({ setPageTitle, refreshKey }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { setPageTitle('Teach Next'); }, [setPageTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await teacherAPI.getClassInsights();
      setData(res);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return <div className="loading" style={{ minHeight: '60vh' }}><div className="loading-spinner" /></div>;
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><FiAlertCircle /></div>
        <div className="empty-state-title">Could not load class insights</div>
        <div className="empty-state-text">{error}</div>
      </div>
    );
  }

  const topics = data?.topics || [];
  const studentCount = data?.studentCount || 0;
  const maxAffected = Math.max(1, ...topics.map((t) => t.studentsAffected));

  return (
    <div>
      {/* Intro */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg,#2997ff,#bf5af2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FiTarget color="#fff" size={22} />
          </div>
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>What to teach next</div>
            <div style={{ opacity: 0.6, fontSize: 14 }}>
              Grammar topics ranked by how many of your {studentCount} students struggle with them.
            </div>
          </div>
        </div>
      </div>

      {topics.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiTarget /></div>
          <div className="empty-state-title">No weak points detected yet</div>
          <div className="empty-state-text">
            Once your students have a few conversations with Carlos, their common
            mistakes will appear here as a ranked lesson-planning list.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {topics.map((t, i) => {
            const isOpen = expanded === t.key;
            const pct = Math.round((t.studentsAffected / maxAffected) * 100);
            const topSev = t.students.some((s) => s.severity === 'high') ? 'high'
              : t.students.some((s) => s.severity === 'medium') ? 'medium' : 'low';
            return (
              <div key={t.key} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header row */}
                <div
                  onClick={() => setExpanded(isOpen ? null : t.key)}
                  style={{ cursor: 'pointer', padding: '18px 20px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {/* Rank */}
                    <div style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      background: 'rgba(255,255,255,0.06)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                      fontSize: 14, color: 'rgba(245,245,247,0.6)',
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontWeight: 600, fontSize: 16 }}>{t.label}</span>
                        <span className="badge badge-info" style={{ flexShrink: 0 }}>{t.cefr}</span>
                      </div>
                      {/* affected bar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <div style={{
                          flex: 1, maxWidth: 320, height: 8,
                          background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${pct}%`, height: '100%', borderRadius: 4,
                            background: SEV_COLOR[topSev],
                          }} />
                        </div>
                        <span style={{ fontSize: 13, opacity: 0.7, display: 'flex', alignItems: 'center', gap: 5 }}>
                          <FiUsers size={13} /> {t.studentsAffected} / {studentCount} students
                        </span>
                      </div>
                    </div>
                    <span style={{ opacity: 0.4, flexShrink: 0 }}>
                      {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                    </span>
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div style={{
                    padding: '0 20px 20px 62px',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    marginTop: -2, paddingTop: 16,
                  }}>
                    {t.lessonHint && (
                      <div style={{
                        padding: '12px 14px', marginBottom: 14,
                        background: 'rgba(41,151,255,0.08)',
                        border: '1px solid rgba(41,151,255,0.2)', borderRadius: 10,
                        fontSize: 14, color: '#7ec0ff',
                      }}>
                        💡 {t.lessonHint}
                      </div>
                    )}
                    <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.5, marginBottom: 10 }}>
                      Students who need this ({t.totalMistakes} total mistakes)
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {t.students.map((s) => (
                        <button
                          key={s.uid}
                          onClick={() => navigate(`/student/${s.uid}`)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            padding: '7px 12px', borderRadius: 100, cursor: 'pointer',
                            background: 'rgba(255,255,255,0.04)',
                            border: `1px solid ${SEV_COLOR[s.severity]}55`,
                            color: 'var(--text-primary, #f5f5f7)', fontSize: 13,
                          }}
                        >
                          <span style={{
                            width: 7, height: 7, borderRadius: '50%',
                            background: SEV_COLOR[s.severity],
                          }} />
                          {s.name}
                          <span style={{ opacity: 0.55, fontWeight: 600 }}>×{s.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ marginTop: 20, fontSize: 12, opacity: 0.4, textAlign: 'center' }}>
        Click a topic to see which students need it · Click a student to open their full profile
      </p>
    </div>
  );
}

export default ClassInsights;
